import { useState } from "react";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
} from "@tanstack/react-query";
import { DateTime } from "luxon";
import type { OrderDetail } from "cadendar-shared/ordering/receipts";
import type { OrdersReceptionApi } from "./orderingApi";
import ReceiptForm from "./ReceiptForm";
import { useOnline } from "./useOnline";

const pendingKey = ["pwa-ordering", "pending"];
const detailKey = (id: string | null) => ["pwa-ordering", "detail", id];
type PendingOrdersData = InfiniteData<
  Awaited<ReturnType<OrdersReceptionApi["listPendingOrders"]>>,
  number
>;

function formatDate(value: string) {
  return DateTime.fromISO(value).toFormat("dd/MM/yyyy HH:mm");
}

export default function OrdersReceptionScreen({
  api,
  isActive,
}: {
  api: OrdersReceptionApi;
  isActive: boolean;
}) {
  const queryClient = useQueryClient();
  const isOnline = useOnline();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const ordersQuery = useInfiniteQuery({
    queryKey: pendingKey,
    queryFn: ({ pageParam }) => api.listPendingOrders(pageParam),
    initialPageParam: 0,
    getNextPageParam: (page) => page.nextOffset,
    enabled: isActive && isOnline,
    retry: false,
  });
  const orderQuery = useQuery({
    queryKey: detailKey(selectedId),
    queryFn: () => {
      if (!selectedId)
        throw new Error("Choisissez une commande à réceptionner.");
      return api.getOrder(selectedId);
    },
    enabled: isActive && isOnline && selectedId !== null,
    retry: false,
  });
  const receiptMutation = useMutation({
    mutationFn: (input: Parameters<OrdersReceptionApi["createReceipt"]>[0]) => {
      if (!navigator.onLine)
        throw new Error(
          "Une connexion est nécessaire pour enregistrer une réception.",
        );
      return api.createReceipt(input);
    },
    retry: false,
    networkMode: "always",
    onSuccess: async (detail: OrderDetail) => {
      queryClient.setQueryData(detailKey(detail.order.id), detail);
      queryClient.setQueryData<PendingOrdersData>(pendingKey, (data) =>
        data
          ? {
              ...data,
              pages: data.pages.map((page) => ({
                ...page,
                orders: page.orders.flatMap((order) =>
                  order.id !== detail.order.id
                    ? [order]
                    : detail.order.status === "received"
                      ? []
                      : [detail.order],
                ),
              })),
            }
          : data,
      );
      setMessage("Réception enregistrée.");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: pendingKey }),
        queryClient.invalidateQueries({ queryKey: detailKey(detail.order.id) }),
      ]);
    },
  });
  const orders = ordersQuery.data?.pages.flatMap((page) => page.orders) ?? [];
  const detail = orderQuery.data;
  return (
    <main className="mx-auto max-w-2xl space-y-5 p-4 text-slate-900">
      <h1 className="text-2xl font-semibold">Réception des commandes</h1>
      {!isOnline && (
        <p role="alert" className="rounded-lg bg-amber-50 p-3 text-amber-900">
          Vous êtes hors ligne. Une connexion est nécessaire pour actualiser les
          commandes et enregistrer une réception.
        </p>
      )}
      {message && (
        <p
          role="status"
          className="rounded-lg bg-emerald-50 p-3 text-emerald-800"
        >
          {message}
        </p>
      )}
      {selectedId ? (
        <>
          <button
            type="button"
            disabled={receiptMutation.isPending}
            className="min-h-11 rounded-lg border border-slate-300 px-4 py-2 disabled:opacity-50"
            onClick={() => {
              setSelectedId(null);
              receiptMutation.reset();
            }}
          >
            ← Retour aux commandes
          </button>
          {isOnline && orderQuery.isPending && (
            <p role="status">Chargement de la commande…</p>
          )}
          {orderQuery.error && (
            <div role="alert" className="space-y-2 text-red-700">
              <p>
                Impossible de charger cette commande :{" "}
                {orderQuery.error.message}
              </p>
              <button
                type="button"
                disabled={!isOnline || orderQuery.isFetching}
                className="min-h-11 rounded border px-3 py-2"
                onClick={() => void orderQuery.refetch()}
              >
                Réessayer
              </button>
            </div>
          )}
          {detail && (
            <section className="space-y-4">
              <div className="rounded-lg bg-slate-100 p-4">
                <h2 className="text-xl font-semibold">
                  {detail.order.supplierName}
                </h2>
                <p className="text-sm text-slate-600">
                  Commande du {formatDate(detail.order.orderedAt)} ·{" "}
                  {detail.order.createdByFullname}
                </p>
                <p className="mt-1 text-sm">
                  {detail.order.receivedQuantity} /{" "}
                  {detail.order.orderedQuantity} unités reçues
                </p>
              </div>
              {receiptMutation.error && (
                <p role="alert" className="text-red-700">
                  Impossible d’enregistrer la réception :{" "}
                  {receiptMutation.error.message}
                </p>
              )}
              {detail.order.status === "received" ? (
                <p className="rounded-lg bg-emerald-50 p-3 text-emerald-800">
                  Cette commande a été entièrement réceptionnée.
                </p>
              ) : (
                <ReceiptForm
                  key={JSON.stringify([
                    detail.order.id,
                    detail.receipts.map((receipt) => receipt.id),
                  ])}
                  order={detail}
                  disabled={!isOnline}
                  isSaving={receiptMutation.isPending}
                  onSubmit={(lines) => {
                    setMessage(null);
                    receiptMutation.mutate({ orderId: detail.order.id, lines });
                  }}
                />
              )}
            </section>
          )}
        </>
      ) : (
        <>
          <p className="text-sm text-slate-600">
            Commandes du cabinet restant à recevoir, y compris les livraisons
            partielles.
          </p>
          <button
            type="button"
            disabled={!isOnline || ordersQuery.isFetching}
            className="min-h-11 rounded-lg border border-slate-300 px-4 py-2 disabled:opacity-50"
            onClick={() => void ordersQuery.refetch()}
          >
            {ordersQuery.isFetching ? "Actualisation…" : "Actualiser"}
          </button>
          {isOnline && ordersQuery.isPending && (
            <p role="status">Chargement des commandes…</p>
          )}
          {ordersQuery.error && (
            <p role="alert" className="text-red-700">
              Impossible de charger les commandes : {ordersQuery.error.message}
            </p>
          )}
          {ordersQuery.isSuccess && orders.length === 0 && (
            <p>Aucune commande à réceptionner.</p>
          )}
          <ul className="space-y-3">
            {orders.map((order) => (
              <li key={order.id}>
                <button
                  type="button"
                  className="min-h-20 w-full space-y-1 rounded-xl border border-slate-300 bg-white p-4 text-left active:bg-blue-50"
                  onClick={() => {
                    receiptMutation.reset();
                    setMessage(null);
                    setSelectedId(order.id);
                  }}
                >
                  <span className="block font-semibold">
                    {order.supplierName}
                  </span>
                  <span className="block text-sm text-slate-600">
                    {formatDate(order.orderedAt)} · {order.createdByFullname}
                  </span>
                  <span className="block text-sm">
                    {order.status === "partially_received"
                      ? "Partiellement reçue"
                      : "À recevoir"}{" "}
                    · {order.orderedQuantity - order.receivedQuantity} unité
                    {order.orderedQuantity - order.receivedQuantity > 1
                      ? "s"
                      : ""}{" "}
                    restante
                    {order.orderedQuantity - order.receivedQuantity > 1
                      ? "s"
                      : ""}
                  </span>
                </button>
              </li>
            ))}
          </ul>
          {ordersQuery.hasNextPage && (
            <button
              type="button"
              disabled={!isOnline || ordersQuery.isFetching}
              className="min-h-12 w-full rounded-lg border border-slate-300 px-4 py-3 disabled:opacity-50"
              onClick={() => void ordersQuery.fetchNextPage()}
            >
              {ordersQuery.isFetchingNextPage
                ? "Chargement…"
                : "Charger les commandes précédentes"}
            </button>
          )}
        </>
      )}
    </main>
  );
}
